import React, { useState, useEffect, memo } from 'react';
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl } from 'react-intl';
// Components
import { Loading, Button } from 'Components';
import VidFleetVideo from 'Modules/.common/components/VidFleetVideo';
import SmartWitnessSnapshot from 'Modules/.common/components/SmartWitnessSnapshot';
// Material UI
import { Grid } from '@mui/material';
import { checkDeviceData } from 'Core/data/Helpers';
import { getSharedMedia } from 'Redux/actions';

const MANUFACTURER = {
  SMART_WITNESS: 'SmartWitness',
  VIDFLEET: 'vidfleet',
  LIGHTMETRICS: 'lightmetrics',
};

const isSmartWitness = (response) =>
  response?.mediaDetail?.unitType === 'switness' ||
  response?.mediaDetail?.deviceModelName === MANUFACTURER.SMART_WITNESS ||
  response?.deviceModelName === MANUFACTURER.SMART_WITNESS;

const isLightMetrics = (response) =>
  response?.mediaDetail?.unitType?.toLowerCase() === MANUFACTURER.LIGHTMETRICS ||
  response?.mediaDetail?.deviceModelName?.toLowerCase() === MANUFACTURER.LIGHTMETRICS ||
  response?.deviceModelName?.toLowerCase() === MANUFACTURER.LIGHTMETRICS;

const NativeMediaPlayer = ({ url, mode = 'video' }) => {
  if (!url) return null;
  const isPicture = mode === 'picture';
  const containerStyle = { width: '100%', maxWidth: 900, height: '100%', margin: '0 auto', padding: 10 };
  if (isPicture) {
    return (
      <div style={containerStyle}>
        <img
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          src={url}
          alt=""
        />
      </div>
    );
  }
  return (
    <div style={containerStyle}>
      <video
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        src={url}
        controls
        playsInline
      />
    </div>
  );
};

const SharedMediaView = (props) => {
  const dispatch = useDispatch();
  const { messages } = props.intl;
  const { id, type } = useParams();
  const { loadingGetSharedMedia: loading, getSharedMediaResponse: response } = useSelector(state => state.sharedMediaRedux);

  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(getSharedMedia({ uuid: id, type }));
    }
  }, [id, type, dispatch]);

  useEffect(() => {
    if (!response) return;
    if (response.error) {
      setError(messages['sharedMedia_notFound']);
      return;
    }

    if (response?.entity?.name === 'DeviceReading') {
      if (!response?.mediaDetail?.deviceData) return;
      try {
        const deviceData = checkDeviceData(response?.mediaDetail?.deviceData);

        if (isLightMetrics(response)) {
          const mediaUrl = deviceData?.url;
          const mode = deviceData?.duration ? 'video' : (deviceData?.pictureID ? 'picture' : 'video');
          setOptions({
            manufacturer: MANUFACTURER.LIGHTMETRICS,
            url: mediaUrl,
            mode,
          });
          return;
        }

        let videoOptions = {
          mode: 'picture',
          id: deviceData?.pictureID,
          pictureId: deviceData?.pictureID,
        };

        if (deviceData?.clipID) {
          if (deviceData?.duration) {
            videoOptions.mode = 'video';
          } else {
            videoOptions.thumbnailUrl = deviceData?.url;
          }
          videoOptions.id = deviceData?.clipID;
          videoOptions.pictureId = null;
        }

        setOptions({
          ...videoOptions,
          messages,
          type: 'playback',
          autoplay: true,
          deviceModel: response?.deviceModelName === '360' ? '360' : '4K',
        });
      } catch (e) {
        console.log('Error while parsing string json view SharedMedia');
      }
      return;
    }

    if (isLightMetrics(response)) {
      const details = response?.mediaDetail?.details;
      let url = null;
      let mode = 'video';
      if (details && typeof details === 'string') {
        try {
          const parsed = JSON.parse(details);
          url = parsed?.url ?? parsed?.videoUrl;
          if (parsed?.mediaType === 'picture' || parsed?.pictureID) mode = 'picture';
        } catch (_) {
          url = details;
        }
      }
      if (!url && response?.mediaDetail?.clipID) {
        url = response?.mediaDetail?.clipID;
      }
      setOptions({
        manufacturer: MANUFACTURER.LIGHTMETRICS,
        url,
        mode,
      });
      return;
    }

    setOptions({
      messages,
      mode: 'video',
      type: 'playback',
      id: response?.mediaDetail?.clipID,
      autoplay: false,
      deviceModel: response?.mediaDetail?.deviceModelName === '360' ? '360' : '4K',
    });
  }, [response, messages]);

  const showSmartWitness = options && isSmartWitness(response);
  const showVidFleet =
    options &&
    !isSmartWitness(response) &&
    !isLightMetrics(response) &&
    options.manufacturer !== MANUFACTURER.LIGHTMETRICS;
  const showLightMetrics = options?.manufacturer === MANUFACTURER.LIGHTMETRICS && options?.url;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {loading ? (
        <Loading full />
      ) : (
        <div style={{ width: '100%', height: '100%', backgroundColor: 'black' }}>
          {showSmartWitness && (
            <div style={{ width: 900, height: '100%', display: 'table-caption', padding: 10 }}>
              <SmartWitnessSnapshot
                height={600}
                indicators={true}
                vehicleTrail={{ deviceData: response?.mediaDetail?.deviceData || response?.mediaDetail?.details }}
              />
            </div>
          )}

          {showVidFleet && <VidFleetVideo {...options} />}

          {showLightMetrics && <NativeMediaPlayer url={options.url} mode={options.mode} />}

          {error && (
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              style={{ padding: 20, height: '100%' }}
            >
              <Grid item>
                <span style={{ fontSize: 18 }}>{error}</span>
              </Grid>
              <Grid item>
                <Button
                  type="icon"
                  icon="refresh"
                  onClick={(e) => {
                    if (e) e.preventDefault();
                    setError(null);
                    dispatch(getSharedMedia({ uuid: id, type }));
                  }}
                />
              </Grid>
            </Grid>
          )}
        </div>
      )}
    </div>
  );
};

export default injectIntl(memo(SharedMediaView));
